import { ClipboardCheck, AlertCircle, Heart, Calendar, ChevronRight, CheckCircle2 } from 'lucide-react';
import { Card, CardHeader, CardContent } from '../core/Card';
import { Button } from '../core/Button';
import { useAuth } from '../../graphql/hooks/useAuth';

export function AdminDashboard() {
  const { userName } = useAuth();

  // Get first name for greeting
  const getFirstName = (fullName: string) => {
    return fullName.split(' ')[0] || fullName;
  };

  const firstName = getFirstName(userName || 'Admin');

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  };

  // Mock data for dashboard tiles
  const dashboardData = {
    openRequests: 12,
    missingTimeEntries: 5,
    newSickNotes: 3,
    monthStatus: 'In Bearbeitung',
    monthProgress: 65, // percentage
  };

  // Action items that require attention
  const actionItems = [
    {
      id: 1,
      type: 'Urlaubsantrag',
      employee: 'Müller, Thomas',
      description: '5 Tage Urlaub vom 13.01. - 17.01.2026',
      urgent: true,
      date: '05.01.2026',
      action: 'anträge',
    },
    {
      id: 2,
      type: 'Krankmeldung',
      employee: 'Schmidt, Anna',
      description: 'Krankmeldung ab 08.01.2026, Attest liegt vor',
      urgent: false,
      date: '08.01.2026',
      action: 'sick-leave',
    },
    {
      id: 3,
      type: 'Zeitkorrektur',
      employee: 'Weber, Klaus',
      description: 'Korrektur für Montag, 06.01.2026',
      urgent: false,
      date: '07.01.2026',
      action: 'anträge',
    },
    {
      id: 4,
      type: 'Fehlende Zeiten',
      employee: 'Meyer, Lisa',
      description: '3 fehlende Zeiteinträge, letzter Eintrag: 03.01.2026',
      urgent: false,
      date: '08.01.2026',
      action: 'time-overview',
    },
    {
      id: 5,
      type: 'Urlaubsantrag',
      employee: 'Fischer, Marc',
      description: '3 Tage Urlaub vom 15.01. - 17.01.2026',
      urgent: false,
      date: '06.01.2026',
      action: 'anträge',
    },
  ];

  const handleNavigate = (page: string) => {
    const event = new CustomEvent('navigate', {
      detail: { page }
    });
    window.dispatchEvent(event);
  };

  const getMonthStatusColor = () => {
    if (dashboardData.monthProgress < 50) return 'bg-red-50 text-red-700 border-red-200';
    if (dashboardData.monthProgress < 80) return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-green-50 text-green-700 border-green-200';
  };

  return (
    <div className="bg-[#f5f5f5] min-h-screen p-4 md:p-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Greeting Section */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-[#1c1c1c] text-[28px] md:text-[32px] font-medium leading-tight mb-2">
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-[#6b6b6b] text-[16px] md:text-[18px]">
            Hier ist Ihre administrative Übersicht für heute
          </p>
        </div>

        {/* Dashboard Tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          {/* Tile: Offene Anträge */}
          <button
            onClick={() => handleNavigate('anträge')}
            className="text-left transition-all hover:scale-[1.02]"
          >
            <Card>
              <CardContent>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="size-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <ClipboardCheck className="size-5 text-blue-700" />
                      </div>
                    </div>
                    <p className="text-[#6b6b6b] text-[14px] font-medium mb-1">Offene Anträge</p>
                    <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium mb-2">
                      {dashboardData.openRequests}
                    </p>
                    <p className="text-[#6b6b6b] text-[13px]">
                      Warten auf Ihre Entscheidung
                    </p>
                  </div>
                  {dashboardData.openRequests > 0 && (
                    <div className="size-2 bg-orange-500 rounded-full mt-2"></div>
                  )}
                </div>
              </CardContent>
            </Card>
          </button>

          {/* Tile: Fehlende Zeiteinträge */}
          <button
            onClick={() => handleNavigate('time-overview')}
            className="text-left transition-all hover:scale-[1.02]"
          >
            <Card>
              <CardContent>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="size-10 bg-amber-50 rounded-lg flex items-center justify-center">
                        <AlertCircle className="size-5 text-amber-700" />
                      </div>
                    </div>
                    <p className="text-[#6b6b6b] text-[14px] font-medium mb-1">Fehlende Zeiteinträge</p>
                    <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium mb-2">
                      {dashboardData.missingTimeEntries}
                    </p>
                    <p className="text-[#6b6b6b] text-[13px]">
                      Mitarbeiter mit Lücken
                    </p>
                  </div>
                  {dashboardData.missingTimeEntries > 0 && (
                    <div className="size-2 bg-amber-500 rounded-full mt-2"></div>
                  )}
                </div>
              </CardContent>
            </Card>
          </button>

          {/* Tile: Neue Krankmeldungen */}
          <button
            onClick={() => handleNavigate('sick-leave')}
            className="text-left transition-all hover:scale-[1.02]"
          >
            <Card>
              <CardContent>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="size-10 bg-red-50 rounded-lg flex items-center justify-center">
                        <Heart className="size-5 text-red-700" />
                      </div>
                    </div>
                    <p className="text-[#6b6b6b] text-[14px] font-medium mb-1">Neue Krankmeldungen</p>
                    <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium mb-2">
                      {dashboardData.newSickNotes}
                    </p>
                    <p className="text-[#6b6b6b] text-[13px]">
                      Zu prüfen und freizugeben
                    </p>
                  </div>
                  {dashboardData.newSickNotes > 0 && (
                    <div className="size-2 bg-red-500 rounded-full mt-2"></div>
                  )}
                </div>
              </CardContent>
            </Card>
          </button>

          {/* Tile: Monatsstatus */}
          <button
            onClick={() => handleNavigate('month-closure')}
            className="text-left transition-all hover:scale-[1.02]"
          >
            <Card>
              <CardContent>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="size-10 bg-green-50 rounded-lg flex items-center justify-center">
                        <Calendar className="size-5 text-green-700" />
                      </div>
                    </div>
                    <p className="text-[#6b6b6b] text-[14px] font-medium mb-1">Monatsstatus</p>
                    <p className="text-[#1c1c1c] text-[20px] font-medium mb-2">
                      {dashboardData.monthStatus}
                    </p>
                    <div className="space-y-1">
                      <p className="text-[#6b6b6b] text-[13px]">
                        Fortschritt: {dashboardData.monthProgress}%
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-[#0084c7] h-2 rounded-full transition-all"
                          style={{ width: `${dashboardData.monthProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>

        {/* Handlungsbedarf Section */}
        <Card>
          <CardHeader
            title="Handlungsbedarf"
            subtitle="Offene Punkte, die Ihre Aufmerksamkeit benötigen"
            borderBottom
          />
          <CardContent>
            {actionItems.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-[10px] p-6 text-center">
                <div className="size-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="size-8 text-green-700" />
                </div>
                <h3 className="text-[#1c1c1c] text-[18px] font-medium mb-2">
                  Keine offenen Aufgaben
                </h3>
                <p className="text-[#6b6b6b] text-[14px] max-w-md mx-auto">
                  Alle administrativen Aufgaben wurden bearbeitet. Es liegen keine Punkte vor, die Ihre Entscheidung benötigen.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {actionItems.map((item) => (
                  <div
                    key={item.id}
                    className={`rounded-[10px] p-4 border transition-all hover:shadow-sm ${
                      item.urgent
                        ? 'bg-orange-50 border-orange-300'
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`size-10 rounded-lg flex items-center justify-center shrink-0 ${
                        item.urgent
                          ? 'bg-orange-100'
                          : 'bg-blue-50'
                      }`}>
                        {item.type === 'Krankmeldung' ? (
                          <Heart className={`size-5 ${item.urgent ? 'text-orange-700' : 'text-blue-700'}`} />
                        ) : item.type === 'Fehlende Zeiten' ? (
                          <AlertCircle className={`size-5 ${item.urgent ? 'text-orange-700' : 'text-blue-700'}`} />
                        ) : (
                          <ClipboardCheck className={`size-5 ${item.urgent ? 'text-orange-700' : 'text-blue-700'}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-medium bg-blue-100 text-blue-700">
                            {item.type}
                          </span>
                          {item.urgent && (
                            <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-700">
                              Dringend
                            </span>
                          )}
                          <span className="text-[#9b9b9b] text-[12px]">
                            {item.date}
                          </span>
                        </div>
                        <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-1">
                          {item.employee}
                        </h3>
                        <p className="text-[#6b6b6b] text-[14px] mb-3">
                          {item.description}
                        </p>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleNavigate(item.action)}
                          icon={<ChevronRight className="size-4" />}
                          iconPosition="right"
                        >
                          Jetzt prüfen
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Section */}
        <div className="mt-6 md:mt-8 bg-blue-50 border border-blue-200 rounded-[10px] p-4">
          <div className="flex items-start gap-3">
            <div className="size-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
              <ClipboardCheck className="size-5 text-blue-700" />
            </div>
            <div className="flex-1">
              <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-1">
                Ihr Admin-Bereich
              </h3>
              <p className="text-[#6b6b6b] text-[14px]">
                In diesem Bereich verwalten Sie ausschließlich administrative Aufgaben: Anträge prüfen und freigeben, Zeitübersichten einsehen, Monatsabschlüsse durchführen und Stammdaten pflegen. Für Ihre eigene Zeiterfassung wechseln Sie bitte in die Benutzeransicht.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
